import { CheckboxCard } from '@design-system';
import { normaliseVariantName } from '@utils/frames';
import type { TFunction } from 'next-i18next';
import { useTranslation } from 'next-i18next';

import { PLACE_HOLDER_IMAGE } from '../../../../constants';
import { Image } from '../../../Image';
import { ProductPrice } from '../../../ProductPrice/ProductPrice';
import { CurrencyCode } from '../types';

const STATIC_THUMBNAILS: Record<string, string> = {
  'float-mount': '/images/float-mount-thumbnail.jpg',
  'window-float-mount': '/images/window-float-mount-thumbnail.jpg',
  'window-mount': '/images/window-mount-thumbnail.jpg',
};

export interface MountingOptionProps {
  groupName: string;
  mounting: string;
  checked: boolean;
  /** Price difference versus the currently selected mounting (0 → no suffix). */
  priceDelta: number;
  /** Per-variant Shopify image; falls back to the static mounting thumbnails. */
  image?: string | null;
  currencyCode?: CurrencyCode;
  onSelect: () => void;
}

const getFeatures = (t: TFunction, mounting: string): string[] | undefined => {
  const value = t(`framing.mountingDetails.${normaliseVariantName(mounting)}`, {
    returnObjects: true,
  }) as unknown;

  return Array.isArray(value)
    ? (value as unknown[]).filter(
        (item): item is string => typeof item === 'string',
      )
    : undefined;
};

export const MountingOption = ({
  checked,
  currencyCode,
  groupName,
  image,
  mounting,
  onSelect,
  priceDelta,
}: MountingOptionProps) => {
  const { t } = useTranslation('checkout');
  const id = `${groupName}-${encodeURIComponent(mounting)}`;
  const fallback =
    STATIC_THUMBNAILS[normaliseVariantName(mounting)] ?? PLACE_HOLDER_IMAGE;
  const showPrice = !checked && priceDelta !== 0;

  return (
    <CheckboxCard
      checked={checked}
      features={getFeatures(t, mounting)}
      id={id}
      label={mounting}
      name={groupName}
      onChange={onSelect}
      slot={
        <div className="grid w-full grid-cols-2 gap-3">
          {image ? (
            <div
              aria-hidden
              className="aspect-[1/1]"
              style={{
                backgroundImage: `url(${image})`,
                backgroundSize: '200% 200%',
                backgroundPosition: '100% 100%',
              }}
            />
          ) : null}
          <Image
            alt={`Image for: ${mounting}`}
            src={image || fallback}
            width={384}
          />
        </div>
      }
      slotVisibleOnChecked
      suffix={
        showPrice ? (
          <ProductPrice
            currencyCode={currencyCode}
            prefix={priceDelta >= 0 ? '+' : '-'}
            price={Math.abs(priceDelta)}
            skipConvertCurrency={!!currencyCode}
            skipTaxCalculation
          />
        ) : undefined
      }
      type="radio"
      value={mounting}
    />
  );
};
